import React from 'react';
import type { Maybe } from '@soundxyz/utils';
import { LexicalDisplay } from '@components/Lexical/Display';
import { PlaylistPlayButton } from '@components/TopPosts/PlaylistPlayButton';
import { ReleasePlayButton } from '@components/TopPosts/ReleasePlayButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { Timestamp } from '@components/basics/Timestamp';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import type {
  PostModalCommentQuery,
  TopPlaylistPostFragment,
  TopReleasePostFragment,
} from '@graphql/reactQuery';
import { useLexicalValidate } from '@hooks/useLexicalValidate';

const PARENT_AVATAR_SIZE = 40;
const AVATAR_SIZE = 16;

function LoadingContent() {
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="h-[14px] w-full animate-pulse rounded-sm bg-neutral200" />
      <div className="h-[14px] w-full animate-pulse rounded-sm bg-neutral200" />
    </div>
  );
}

function LoadingAuthor() {
  return <div className="h-5 w-48 animate-pulse rounded-sm bg-neutral200" />;
}

type BaseProps = {
  author: Maybe<NonNullable<PostModalCommentQuery['postComment']>['author']>;
  content: Maybe<NonNullable<PostModalCommentQuery['postComment']>['content']>;
  createdAt: Maybe<string>;
  isLoading: boolean;
  lexicalContent: Maybe<NonNullable<PostModalCommentQuery['postComment']>['lexicalContent']>;
};

export type ReleaseVariant = {
  variant: 'release';
  optionalRelease: TopReleasePostFragment['optionalRelease'];
};

export type PlaylistVariant = {
  variant: 'playlist';
  optionalShelf: TopPlaylistPostFragment['optionalShelf'];
};

export type DefaultVariant = {
  variant: 'default';
};

type Variants = DefaultVariant | ReleaseVariant | PlaylistVariant;

export function ParentComment(props: BaseProps & Variants) {
  const { author, content, createdAt, isLoading, lexicalContent, variant } = props;

  const lexical = useLexicalValidate(lexicalContent);

  return (
    <div className="flex w-full gap-3">
      {!!author && !isLoading ? (
        <div className="h-10 w-10 flex-shrink-0">
          <Avatar
            size={PARENT_AVATAR_SIZE}
            walletAddress={author.publicAddress}
            webappUri={author.webappUri}
            src={author.avatar?.url}
            username={author.username}
            borderRadius={PARENT_AVATAR_SIZE}
          />
        </div>
      ) : (
        <div className="h-10 w-10 animate-pulse rounded-full bg-neutral200" />
      )}

      <div className="flex w-full flex-col gap-1.5">
        <div className="flex items-center gap-2">
          {author && createdAt && !isLoading ? (
            <>
              <span className="font-base text-base-m font-semibold text-base900">
                {author.username}
              </span>{' '}
              {!!author.artist && <VerifiedTooltip size={16} portal noTooltip />}
              <Timestamp
                timeStamp={createdAt}
                className="font-base !text-base-xs font-normal text-base500 duration-300 hover:text-base600"
              />
            </>
          ) : (
            <LoadingAuthor />
          )}
        </div>

        {!isLoading ? (
          <>
            <div className="line-clamp-4">
              {lexical ? <LexicalDisplay showMentionsHoverCard={false} state={lexical} /> : content}
            </div>
            {variant === 'release' && <ReleasePlayButton release={props.optionalRelease} />}
            {variant === 'playlist' && <PlaylistPlayButton playlist={props.optionalShelf} />}
          </>
        ) : (
          <LoadingContent />
        )}
      </div>
    </div>
  );
}

export function Comment({
  author,
  content,
  createdAt,
  isLoading,
  lexicalContent,
}: {
  author: Maybe<NonNullable<PostModalCommentQuery['postComment']>['author']>;
  content: Maybe<NonNullable<PostModalCommentQuery['postComment']>['content']>;
  createdAt: Maybe<string>;
  isLoading: boolean;
  lexicalContent: Maybe<NonNullable<PostModalCommentQuery['postComment']>['lexicalContent']>;
}) {
  const lexical = useLexicalValidate(lexicalContent);

  return (
    <div className="flex w-full gap-3 pl-[48px]">
      <div className="flex w-full flex-col gap-1.5">
        <div className="flex items-center gap-2">
          {author && createdAt && !isLoading ? (
            <>
              <div className="h-4 w-4 flex-shrink-0">
                <Avatar
                  size={AVATAR_SIZE}
                  walletAddress={author.publicAddress}
                  webappUri={author.webappUri}
                  src={author.avatar?.url}
                  username={author.username}
                  borderRadius={AVATAR_SIZE}
                />
              </div>
              <span className="font-base text-base-m font-semibold text-base900">
                {author.username}
              </span>{' '}
              {!!author.artist && <VerifiedTooltip size={16} portal noTooltip />}
              <Timestamp
                timeStamp={createdAt}
                className="font-base !text-base-xs font-normal text-base500 duration-300 hover:text-base600"
              />
            </>
          ) : (
            <>
              <div className="h-4 w-4 animate-pulse rounded-full bg-neutral200" />
              <LoadingAuthor />
            </>
          )}
        </div>

        {!isLoading ? (
          <div className="line-clamp-4">
            {lexical ? <LexicalDisplay showMentionsHoverCard={false} state={lexical} /> : content}
          </div>
        ) : (
          <LoadingContent />
        )}
      </div>
    </div>
  );
}
